import { useState, useEffect, useRef } from 'react';
import { Box, HStack, Textarea, IconButton, useBreakpointValue } from '@chakra-ui/react';
import { MdSend as SendIcon, MdMic as MicIcon, MdStop as StopIcon } from 'react-icons/md';
import { useVoiceInput } from '@/hooks/useVoiceInput';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  /** When this increases on desktop, focus returns to the input. */
  messageCount?: number;
  /** Incremented when the action card is dismissed — refocuses input on desktop. */
  focusTrigger?: number;
}

export default function ChatInput({
  onSend,
  isLoading = false,
  placeholder = 'Message',
  messageCount = 0,
  focusTrigger = 0,
}: ChatInputProps) {
  const [inputMessage, setInputMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevFocusTrigger = useRef(focusTrigger);
  const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });

  const { isRecording, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    onTranscriptionComplete: (text) => {
      setInputMessage((prev) => {
        const separator = prev.trim() ? ' ' : '';
        return prev + separator + text;
      });
    },
  });

  // Focus the input on mount (desktop only). setTimeout(0) lets the Chakra
  // Dialog focus trap settle before we override it.
  useEffect(() => {
    if (!isDesktop) return;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-focus when a new message is added (desktop only).
  useEffect(() => {
    if (!isDesktop || !messageCount) return;
    inputRef.current?.focus();
  }, [messageCount, isDesktop]);

  useEffect(() => {
    return () => {
      if (isRecording) cancelRecording();
    };
  }, [isRecording, cancelRecording]);

  // Focus textarea when action card is dismissed (desktop only).
  useEffect(() => {
    if (focusTrigger === prevFocusTrigger.current) return;
    prevFocusTrigger.current = focusTrigger;
    if (isDesktop) inputRef.current?.focus();
  }, [focusTrigger, isDesktop]);

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;
    onSend(inputMessage);
    setInputMessage('');
    if (isDesktop) inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      w="100%"
      px={3}
      pt={3}
      pb="calc(env(safe-area-inset-bottom, 0px) + 16px)"
      flexShrink={0}
      bg="bg.canvas"
    >
      {/* Pill container */}
      <HStack
        bg="white"
        border="2px solid"
        borderColor="border.default"
        borderRadius="full"
        px={1}
        py="4px"
        gap={1}
        align="center"
      >
        <IconButton
          aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
          size="sm"
          colorPalette={isRecording ? 'red' : 'gray'}
          variant={isRecording ? 'solid' : 'ghost'}
          borderRadius="full"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isLoading}
          flexShrink={0}
        >
          {isRecording ? <StopIcon /> : <MicIcon />}
        </IconButton>
        <Textarea
          ref={inputRef}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="sm"
          resize="none"
          rows={1}
          minH="40px"
          maxH="120px"
          disabled={isLoading || isRecording}
          border="none"
          bg="transparent"
          _focus={{ outline: 'none', boxShadow: 'none' }}
          px={2}
          py={0}
          flex={1}
          lineHeight="40px"
        />
        <IconButton
          aria-label="Send message"
          size="sm"
          bg="accent.emphasis"
          color="white"
          borderRadius="full"
          _hover={{ bg: 'accent.emphasisHover' }}
          onClick={handleSendMessage}
          loading={isLoading}
          disabled={!inputMessage.trim() || isLoading || isRecording}
          flexShrink={0}
        >
          <SendIcon />
        </IconButton>
      </HStack>
    </Box>
  );
}
